
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Event } from "@/types/event";

export const useEventUpdate = (eventId: string, onSuccess: () => void) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updatedEvent: Partial<Event>) => {
      console.log('Updating event with data:', updatedEvent);
      console.log('Deadline value being sent to database:', updatedEvent.event_deadline);
      
      // First, fetch the current event data to merge with the updates
      const { data: currentEvent, error: fetchError } = await supabase
        .from('events')
        .select('*')
        .eq('event_id', eventId)
        .single();
        
      if (fetchError) {
        console.error('Error fetching current event data:', fetchError);
        throw fetchError;
      }
      
      console.log('Current event data from database:', {
        name: currentEvent.name,
        deadline: currentEvent.event_deadline
      });
      
      // Merge the current event data with the updates
      // This ensures we're not overwriting fields with undefined
      const updateData = {
        ...currentEvent,
        ...updatedEvent,
        // Special handling for specific fields that need different merging logic
        preferred_venues: updatedEvent.preferred_venues !== undefined 
          ? updatedEvent.preferred_venues 
          : currentEvent.preferred_venues,
        preferred_dates: updatedEvent.preferred_dates !== undefined 
          ? updatedEvent.preferred_dates 
          : currentEvent.preferred_dates,
        preferred_time_of_day: updatedEvent.preferred_time_of_day !== undefined 
          ? updatedEvent.preferred_time_of_day 
          : currentEvent.preferred_time_of_day,
        // Special handling for event_deadline field - ensure it's properly included
        event_deadline: updatedEvent.event_deadline !== undefined
          ? updatedEvent.event_deadline
          : currentEvent.event_deadline
      };
      
      // Remove specific fields that shouldn't be sent in the update
      delete updateData.created_at;
      delete updateData.updated_at;
      delete updateData.event_id;
      
      // Remove derived fields (not actual database columns)
      if ('host' in updateData) delete updateData.host;
      if ('attendees' in updateData) delete updateData.attendees;
      if ('event_attendee' in updateData) delete updateData.event_attendee;
      if ('selectedVenue' in updateData) delete updateData.selectedVenue;
      if ('selectedDate' in updateData) delete updateData.selectedDate;
      if ('event_date_recommendations' in updateData) delete updateData.event_date_recommendations;
      if ('event_venue_recommendations' in updateData) delete updateData.event_venue_recommendations;
      if ('event_images' in updateData) delete updateData.event_images;
      if ('additional_images' in updateData) delete updateData.additional_images;
      
      console.log('Merged update payload:', updateData);
      console.log('Final deadline value being sent:', updateData.event_deadline);

      // Start a transaction to update the event and handle image changes
      const { data, error: eventError } = await supabase
        .from('events')
        .update(updateData)
        .eq('event_id', eventId)
        .select();

      if (eventError) {
        console.error('Error updating event:', eventError);
        throw eventError;
      }

      console.log('Response from update operation:', data);

      // Handle event images
      if (updatedEvent.additional_images && updatedEvent.additional_images.length > 0) {
        console.log('Updating event images:', updatedEvent.additional_images);
        
        // First, delete all existing images for this event
        const { error: deleteError } = await supabase
          .from('event_images')
          .delete()
          .eq('event_id', eventId);
          
        if (deleteError) {
          console.error('Error deleting existing images:', deleteError);
          throw deleteError;
        }
        
        // Then insert all images from the updated event
        const imagesToInsert = updatedEvent.additional_images.map((img: any, index: number) => ({
          event_id: eventId,
          image_url: img.image_url,
          is_primary: img.is_primary,
          display_order: img.display_order || index
        }));
        
        const { error: insertError } = await supabase
          .from('event_images')
          .insert(imagesToInsert);
          
        if (insertError) {
          console.error('Error inserting updated images:', insertError);
          throw insertError;
        }
      }

      return { success: true };
    },
    onSuccess: () => {
      // Invalidate the event query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      toast({
        title: "Success",
        description: "Event updated successfully",
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update event",
        variant: "destructive",
      });
      console.error('Error updating event:', error);
    },
  });
};
